import React, { useState, useEffect } from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import ClientesPorEstadoModal from './ClientesPorEstadoModal'

interface EstadoData {
  uf: string
  nomeEstado: string
  regiao: string
  totalClientes: number
  clientesAtivos: number
  clientesInativos: number
  percentualDoTotal: number
}

interface ClientesPorRegiaoData {
  estados: EstadoData[]
  totalGeral: number
  totalEstados: number
}

const ClientesPorRegiaoCard: React.FC = () => {
  const [data, setData] = useState<ClientesPorRegiaoData | null>(null)
  const [loading, setLoading] = useState(true)
  const [showAll, setShowAll] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [estadoSelecionado, setEstadoSelecionado] = useState<any>(null)
  const [clientesDoEstado, setClientesDoEstado] = useState<any[]>([])
  const [loadingClientes, setLoadingClientes] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('🔍 Buscando dados de clientes por região...')
        const response = await fetch('/api/gestao/dashboard/clientes-por-regiao')
        console.log('📡 Response status:', response.status)
        
        const result = await response.json()
        console.log('📊 Dados recebidos:', result)
        
        if (result.success) {
          console.log('✅ Dados carregados com sucesso:', result.data.estados.length, 'estados')
          setData(result.data)
        } else {
          console.error('❌ API retornou erro:', result.error)
        }
      } catch (error) {
        console.error('❌ Erro ao carregar clientes por região:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const handleEstadoClick = async (uf: string) => {
    const estado = data?.estados.find(e => e.uf === uf)
    if (!estado) return

    setLoadingClientes(true)
    try {
      const response = await fetch(`/api/gestao/dashboard/clientes-por-estado?uf=${uf}`)
      const result = await response.json()
      
      if (result.success) {
        setEstadoSelecionado(result.data)
        setClientesDoEstado(result.data.clientes)
        setModalOpen(true)
      }
    } catch (error) {
      console.error('Erro ao buscar clientes do estado:', error)
    } finally {
      setLoadingClientes(false)
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-8 bg-gray-200 rounded"></div>
            <div className="h-8 bg-gray-200 rounded"></div>
            <div className="h-8 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  if (!data || data.estados.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-800">Clientes por Região</h2>
        </div>
        <p className="text-gray-500 text-center py-8">Nenhum dado disponível</p>
      </div>
    )
  }

  const estadosParaMostrar = showAll ? data.estados : data.estados.slice(0, 5)
  const coresGradiente = [
    '#10b981', // green-500
    '#3b82f6', // blue-500
    '#8b5cf6', // purple-500
    '#ec4899', // pink-500
    '#f59e0b', // amber-500
  ]

  // Preparar dados para o gráfico
  const dadosGrafico = estadosParaMostrar.map((estado, index) => ({
    nome: estado.uf,
    total: estado.totalClientes,
    ativos: estado.clientesAtivos,
    inativos: estado.clientesInativos,
    cor: coresGradiente[index % coresGradiente.length]
  }))

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload
      const estadoCompleto = estadosParaMostrar.find(e => e.uf === data.nome)
      
      return (
        <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200">
          <p className="font-bold text-gray-800 mb-2">{estadoCompleto?.nomeEstado}</p>
          <p className="text-sm text-gray-600">Região: {estadoCompleto?.regiao}</p>
          <div className="mt-2 space-y-1">
            <p className="text-sm">
              <span className="font-semibold text-green-600">Ativos:</span> {data.ativos}
            </p>
            <p className="text-sm">
              <span className="font-semibold text-red-600">Inativos:</span> {data.inativos}
            </p>
            <p className="text-sm font-bold text-gray-800 pt-1 border-t">
              Total: {data.total} ({estadoCompleto?.percentualDoTotal}%)
            </p>
          </div>
        </div>
      )
    }
    return null
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">Clientes por Região</h2>
            <p className="text-sm text-gray-500">{data.totalEstados} estados • {data.totalGeral} clientes</p>
          </div>
        </div>
      </div>

      {/* Gráfico de Barras */}
      <div className="mb-6">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart 
            data={dadosGrafico} 
            layout="vertical"
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis type="number" stroke="#6b7280" />
            <YAxis dataKey="nome" type="category" stroke="#6b7280" width={50} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
            <Bar 
              dataKey="total" 
              radius={[0, 8, 8, 0]} 
              barSize={20}
              onClick={(data: any) => handleEstadoClick(data.nome)}
              cursor="pointer"
            >
              {dadosGrafico.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.cor} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Lista de Estados */}
      <div className="space-y-2">
        {estadosParaMostrar.map((estado, index) => (
          <div 
            key={estado.uf}
            className="flex items-center justify-between p-3 rounded-lg hover:bg-blue-50 transition-colors cursor-pointer"
            onClick={() => handleEstadoClick(estado.uf)}
          >
            <div className="flex items-center gap-3 flex-1">
              <div 
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: coresGradiente[index % coresGradiente.length] }}
              ></div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-800">{estado.uf}</span>
                  <span className="text-sm text-gray-500">{estado.nomeEstado}</span>
                </div>
                <span className="text-xs text-gray-400">{estado.regiao}</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-sm font-semibold text-gray-800">{estado.totalClientes}</div>
                <div className="text-xs text-gray-500">{estado.percentualDoTotal}%</div>
              </div>
              <div className="flex gap-2 text-xs">
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                  {estado.clientesAtivos} ativos
                </span>
                {estado.clientesInativos > 0 && (
                  <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full font-medium">
                    {estado.clientesInativos} inativos
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Botão Ver Mais/Menos */}
      {data.estados.length > 5 && (
        <div className="mt-4 pt-4 border-t">
          <button
            onClick={() => setShowAll(!showAll)}
            className="w-full px-4 py-2 text-sm font-medium text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {showAll ? (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
                Ver menos
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                Ver todos os {data.estados.length} estados
              </>
            )}
          </button>
        </div>
      )}

      {/* Modal de Clientes por Estado */}
      {modalOpen && estadoSelecionado && (
        <ClientesPorEstadoModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          uf={estadoSelecionado.uf}
          nomeEstado={estadoSelecionado.nomeEstado}
          regiao={estadoSelecionado.regiao}
          clientes={clientesDoEstado}
          totalClientes={estadoSelecionado.totalClientes}
          clientesAtivos={estadoSelecionado.clientesAtivos}
          clientesInativos={estadoSelecionado.clientesInativos}
        />
      )}
    </div>
  )
}

export default ClientesPorRegiaoCard
